import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

/*
 * Stylised KL skyline silhouette: Petronas Twin Towers (centre), KL Tower
 * (right of centre), Bursa / Exchange Square block (left), TRX-style tower
 * (far right), and a few mid-rise neighbours. Sits behind the hero and on
 * the Race Day venue card.
 *
 * Painted with currentColor so it adapts to whichever surface it sits on.
 * Default tone is the secondary token (Bursa navy in bursa-edge), kept at
 * low opacity so headline copy reads cleanly on top.
 *
 * Slot prop `artwork` lets a designer-supplied PNG/SVG replace the built-in
 * silhouette without any other code edit.
 */
export type KLSkylineProps = {
  className?: string;
  /** Tailwind text-color class. Defaults to secondary (navy) at 18% opacity. */
  toneClassName?: string;
  artwork?: ReactNode;
};

export function KLSkyline({
  className,
  toneClassName = "text-secondary/20",
  artwork
}: KLSkylineProps) {
  if (artwork) {
    return (
      <div aria-hidden className={cn("pointer-events-none", className)}>
        {artwork}
      </div>
    );
  }

  return (
    <svg
      aria-hidden
      viewBox="0 0 800 240"
      preserveAspectRatio="xMidYEnd meet"
      className={cn("pointer-events-none w-full", toneClassName, className)}
      fill="currentColor"
    >
      {/* Distant low-rise haze */}
      <g opacity="0.45">
        <rect x="0" y="172" width="60" height="68" />
        <rect x="68" y="160" width="44" height="80" />
        <rect x="118" y="178" width="36" height="62" />
        <rect x="640" y="170" width="56" height="70" />
        <rect x="702" y="184" width="42" height="56" />
        <rect x="750" y="174" width="50" height="66" />
      </g>

      {/*
       * Bursa Malaysia HQ, Exchange Square (left-of-centre) — traced from the
       * front-elevation reference. Wide, symmetric, monumental facade (centre
       * line x=212): a flat full-width parapet broken only by a single central
       * triangular pediment, a colossal-column order across the full width, an
       * upper band of round-arched windows, a central projecting pedimented
       * entrance, and a ground-level arcade. Glass / arches are cut with the
       * background colour so the hero surface shows through, matching the
       * Petronas / TRX convention. (Replaces the former adjacent mid-rise
       * filler, which this now occupies.)
       */}
      <g>
        {/* Podium / base */}
        <rect x="136" y="214" width="152" height="26" />
        {/* Full-width main body — flat parapet top */}
        <rect x="140" y="138" width="144" height="102" />
        {/* Top cornice band (runs straight to both edges) */}
        <rect x="137" y="136" width="150" height="4" />
        {/* Single central triangular pediment on the flat parapet + finial */}
        <rect x="183" y="134" width="58" height="3" />
        <polygon points="186,137 212,114 238,137" />
        <rect x="211" y="110" width="2" height="5" />
        {/* Glass / arcade detail (background shows through) */}
        <g fill="hsl(var(--background))" opacity="0.3">
          {/* Colossal column order across the full facade */}
          <rect x="146" y="172" width="2" height="34" />
          <rect x="154" y="172" width="2" height="34" />
          <rect x="162" y="172" width="2" height="34" />
          <rect x="170" y="172" width="2" height="34" />
          <rect x="178" y="172" width="2" height="34" />
          <rect x="186" y="172" width="2" height="34" />
          <rect x="194" y="172" width="2" height="34" />
          <rect x="202" y="172" width="2" height="34" />
          <rect x="210" y="172" width="2" height="34" />
          <rect x="218" y="172" width="2" height="34" />
          <rect x="226" y="172" width="2" height="34" />
          <rect x="234" y="172" width="2" height="34" />
          <rect x="242" y="172" width="2" height="34" />
          <rect x="250" y="172" width="2" height="34" />
          <rect x="258" y="172" width="2" height="34" />
          <rect x="266" y="172" width="2" height="34" />
          <rect x="274" y="172" width="2" height="34" />
          {/* Upper band of round-arched windows */}
          <path d="M146,168 L146,162 Q150.5,156 155,162 L155,168 Z" />
          <path d="M158,168 L158,162 Q162.5,156 167,162 L167,168 Z" />
          <path d="M170,168 L170,162 Q174.5,156 179,162 L179,168 Z" />
          <path d="M182,168 L182,162 Q186.5,156 191,162 L191,168 Z" />
          <path d="M194,168 L194,162 Q198.5,156 203,162 L203,168 Z" />
          <path d="M206,168 L206,162 Q210.5,156 215,162 L215,168 Z" />
          <path d="M218,168 L218,162 Q222.5,156 227,162 L227,168 Z" />
          <path d="M230,168 L230,162 Q234.5,156 239,162 L239,168 Z" />
          <path d="M242,168 L242,162 Q246.5,156 251,162 L251,168 Z" />
          <path d="M254,168 L254,162 Q258.5,156 263,162 L263,168 Z" />
          <path d="M266,168 L266,162 Q270.5,156 275,162 L275,168 Z" />
          {/* Ground-level arcade (skips the central entrance) */}
          <path d="M144,238 L144,224 Q150.5,216 157,224 L157,238 Z" />
          <path d="M161,238 L161,224 Q167.5,216 174,224 L174,238 Z" />
          <path d="M178,238 L178,224 Q184.5,216 191,224 L191,238 Z" />
          <path d="M230,238 L230,224 Q236.5,216 243,224 L243,238 Z" />
          <path d="M247,238 L247,224 Q253.5,216 260,224 L260,238 Z" />
          <path d="M264,238 L264,224 Q270.5,216 277,224 L277,238 Z" />
        </g>
        {/* Central projecting pedimented entrance */}
        <rect x="196" y="198" width="32" height="42" />
        <rect x="192" y="196" width="40" height="4" />
        <polygon points="194,196 212,184 230,196" />
        <g fill="hsl(var(--background))" opacity="0.3">
          <path d="M203,240 L203,218 Q212,208 221,218 L221,240 Z" />
          <rect x="199" y="200" width="2" height="30" />
          <rect x="225" y="200" width="2" height="30" />
        </g>
      </g>

      {/* Petronas Twin Towers — the centrepiece */}
      <g>
        {/* Left tower */}
        <rect x="316" y="40" width="42" height="200" />
        <polygon points="316,40 337,16 358,40" />
        <rect x="334" y="0" width="6" height="22" />
        {/* Skybridge */}
        <rect x="358" y="118" width="42" height="10" />
        <rect x="372" y="106" width="2" height="12" />
        <rect x="384" y="106" width="2" height="12" />
        {/* Right tower */}
        <rect x="400" y="40" width="42" height="200" />
        <polygon points="400,40 421,16 442,40" />
        <rect x="418" y="0" width="6" height="22" />
        {/* Window strokes for vertical texture */}
        <g fill="hsl(var(--background))" opacity="0.25">
          <rect x="316" y="60" width="42" height="2" />
          <rect x="316" y="80" width="42" height="2" />
          <rect x="316" y="100" width="42" height="2" />
          <rect x="316" y="140" width="42" height="2" />
          <rect x="316" y="160" width="42" height="2" />
          <rect x="316" y="180" width="42" height="2" />
          <rect x="316" y="200" width="42" height="2" />
          <rect x="400" y="60" width="42" height="2" />
          <rect x="400" y="80" width="42" height="2" />
          <rect x="400" y="100" width="42" height="2" />
          <rect x="400" y="140" width="42" height="2" />
          <rect x="400" y="160" width="42" height="2" />
          <rect x="400" y="180" width="42" height="2" />
          <rect x="400" y="200" width="42" height="2" />
        </g>
      </g>

      {/* KL Tower (Menara KL) — long slim shaft, with the observation head/pod
          ("node") sitting high up the tower: a flared muqarnas collar + balcony,
          a clean rounded pod node, and a tall antenna with a spindle bulge above. */}
      <g>
        {/* Long slender shaft (head sits high, so the shaft below is long) */}
        <rect x="496" y="112" width="6" height="128" />
        {/* Flared muqarnas collar (corbel) — widens up to the balcony */}
        <polygon points="496,112 502,112 511,104 487,104" />
        {/* Observation-deck balcony rim */}
        <rect x="487" y="101" width="24" height="3" />
        {/* Rounded pod node */}
        <path d="M490,102 C486,95 490,88 499,88 C508,88 512,95 508,102 Z" />
        {/* Antenna: lower mast, spindle bulge, fine tip */}
        <rect x="498" y="60" width="2" height="28" />
        <ellipse cx="499" cy="54" rx="2.3" ry="5" />
        <rect x="498.2" y="36" width="1.6" height="18" />
      </g>

      {/* Mid-rise neighbour */}
      <rect x="528" y="156" width="46" height="84" />

      {/* TRX-style tower (far right) */}
      <g>
        <rect x="586" y="72" width="40" height="168" />
        <polygon points="586,72 606,52 626,72" />
        <rect x="604" y="38" width="4" height="16" />
        <g fill="hsl(var(--background))" opacity="0.28">
          <rect x="586" y="92" width="40" height="2" />
          <rect x="586" y="112" width="40" height="2" />
          <rect x="586" y="132" width="40" height="2" />
          <rect x="586" y="152" width="40" height="2" />
          <rect x="586" y="172" width="40" height="2" />
          <rect x="586" y="192" width="40" height="2" />
          <rect x="586" y="212" width="40" height="2" />
        </g>
      </g>
    </svg>
  );
}
